import * as React from "react";
import { createPortal } from "react-dom";
import * as styles from "./ModuleDetailPage.css";

const VIEWPORT_PAD = 8;
const GAP = 10;
/** Match MediaUploader gallery thumbnail hover-preview delay. */
const OPEN_DELAY_MS = 800;
const CLOSE_DELAY_MS = 120;

export function CoursePreviewHoverButton({
  label,
  icon,
  children,
  previewClassName,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  /** Extra class on the popover panel (e.g. card vs hero width). */
  previewClassName?: string;
}) {
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const closeTimer = React.useRef<number | null>(null);

  const clearOpenTimer = React.useCallback(() => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const clearTimers = React.useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
  }, [clearOpenTimer, clearCloseTimer]);

  const showPreview = React.useCallback(() => {
    clearTimers();
    setOpen(true);
  }, [clearTimers]);

  const scheduleOpen = React.useCallback(() => {
    clearCloseTimer();
    if (open) return;
    clearOpenTimer();
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  }, [clearCloseTimer, clearOpenTimer, open]);

  const scheduleClose = React.useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, [clearOpenTimer, clearCloseTimer]);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  React.useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const width = popover?.offsetWidth ?? 320;
      const height = popover?.offsetHeight ?? 0;

      let left = rect.right - width;
      let top = rect.bottom + GAP;

      if (left + width > window.innerWidth - VIEWPORT_PAD) {
        left = window.innerWidth - width - VIEWPORT_PAD;
      }
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

      if (top + height > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(VIEWPORT_PAD, rect.top - height - GAP);
      }

      setPosition({ top, left });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    const popover = popoverRef.current;
    const ro = popover
      ? new ResizeObserver(() => updatePosition())
      : null;
    if (popover && ro) ro.observe(popover);

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.previewIconButton}
        aria-label={label}
        title={label}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
      >
        {icon}
      </button>

      {open
        ? createPortal(
            <div
              ref={popoverRef}
              role="tooltip"
              className={[styles.previewPopover, previewClassName].filter(Boolean).join(" ")}
              style={
                position
                  ? { top: position.top, left: position.left, opacity: 1 }
                  : { top: 0, left: 0, opacity: 0, pointerEvents: "none" }
              }
              onMouseEnter={showPreview}
              onMouseLeave={scheduleClose}
            >
              <p className={styles.previewPopoverLabel}>{label}</p>
              <div className={styles.previewPopoverBody}>{children}</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
